"use client";
import { resizeImageUrl } from "@lib/util/images";
import type { StoreProductCategory } from "@medusajs/types";
import { clx } from "@medusajs/ui";
import PlaceholderImage from "@modules/common/icons/placeholder-image";
import { ChevronDown as Down, Grid } from "lucide-react";
import Image from "next/image";
import Link from "next/link";
import { useEffect, useRef, useState } from "react";

type Category = {
  [x: string]: any;
  id: string;
  name: string;
  handle: string;
  parent_category_id: string | null;
  category_children?: Category[];
};

export function buildCategoryTree(categories: Category[]): Category[] {
  const categoryMap: Record<string, Category> = {};
  const tree: Category[] = [];

  categories?.forEach((category) => {
    categoryMap[category.id] = { ...category, category_children: [] };
  });

  categories?.forEach((category) => {
    if (category.parent_category_id) {
      categoryMap[category.parent_category_id]?.category_children?.push(categoryMap[category.id]);
    } else {
      tree.push(categoryMap[category.id]);
    }
  });

  return tree;
}

function CategoryDropdown({ productCategories }: { productCategories: StoreProductCategory[] }) {
  const [isOpen, setIsOpen] = useState(false);
  const [hoveredCategory, setHoveredCategory] = useState<string | null>(null);
  const dropdownRef = useRef<HTMLDivElement>(null);

  const categories = buildCategoryTree(productCategories);

  const parentCategory = categories.find((cat) => cat.id === hoveredCategory);

  useEffect(() => {
    const handleClickOutside = (event: MouseEvent) => {
      if (dropdownRef.current && !dropdownRef.current.contains(event.target as Node)) {
        setIsOpen(false);
        setHoveredCategory(null);
      }
    };
    document.addEventListener("mousedown", handleClickOutside);
    return () => {
      document.removeEventListener("mousedown", handleClickOutside);
    };
  }, []);

  const numberOfColumns = Math.min(parentCategory?.category_children?.length ?? 0, 4);

  return (
    <div ref={dropdownRef} className="relative">
      {isOpen && (
        <div className="fixed inset-0 bg-black/40 backdrop-blur-none z-10" onClick={() => setIsOpen(false)}></div>
      )}
      <div
        className="flex items-center px-3 py-2 border  rounded-xl gap-2 cursor-pointer z-50"
        onClick={() => setIsOpen(!isOpen)}
      >
        <div className="flex items-center gap-2">
          <Grid className="w-6 h-6 " />
          <span className="font-semibold  text-[14px] select-none">Danh mục sản phẩm</span>
        </div>
        <Down className="w-5 h-5" />
      </div>

      <div
        className={`absolute left-0 mt-2 bg-white shadow-lg rounded-lg z-10 min-w-max w-full flex transition-all duration-300 ease-in-out ${
          isOpen ? "opacity-100 scale-100" : "opacity-0 scale-95 pointer-events-none"
        }`}
        onMouseLeave={() => setHoveredCategory(null)}
      >
        <div className="w-[222px] max-h-[500px] overflow-y-auto">
          {categories.map((category) => (
            <Link
              href={`/categories/${category.handle}`}
              key={category.id}
              className="group"
              onMouseEnter={() => setHoveredCategory(category.id)}
            >
              <div
                className={clx(
                  "flex items-center gap-2 px-1 text-[13px] hover:bg-gray-200 rounded-md cursor-pointer font-semibold m-1 transition-all duration-200 hover:text-primary-500",
                  {
                    "bg-gray-200": hoveredCategory == category.id,
                  }
                )}
              >
                <div className="rounded-md h-[32px] flex items-center">
                  {typeof category?.metadata?.thumbnail === "string" ? (
                    <Image
                      src={resizeImageUrl(category.metadata.thumbnail, 22, 22)}
                      alt="Product Thumbnail"
                      width={22}
                      height={22}
                      unoptimized
                    />
                  ) : (
                    <PlaceholderImage size={22} color="gray" />
                  )}
                </div>
                {category.name}
              </div>
            </Link>
          ))}
        </div>

        {hoveredCategory !== null && (
          <div
            className={`grid grid-cols-2 w-fit max-w-[900px] 2xl:grid-cols-${numberOfColumns} xl:grid-cols-4 gap-5 pl-4 p-3 border-l transition-all duration-300 ease-in-out overflow-y-auto max-h-[500px]`}
          >
            {parentCategory?.category_children?.map((sub) => (
              <div key={sub.id}>
                <Link
                  href={`/categories/${sub.handle}`}
                  className="py-1 text-[13px] text-[#363636] font-semibold w-[220px] hover:text-primary-500"
                >
                  {sub.name}
                </Link>
                <div className="flex flex-col">
                  {sub.category_children?.map((child) => (
                    <Link
                      href={`/categories/${child.handle}`}
                      key={child.id}
                      className="text-[13px] text-[#707070] hover:text-primary-500"
                    >
                      {child.name}
                    </Link>
                  ))}
                </div>
              </div>
            ))}
          </div>
        )}
      </div>
    </div>
  );
}

export default CategoryDropdown;
